import * as React from 'react';
import {
  List,
  Edit,
  Create,
  SimpleForm,
  FunctionField,
  EditButton,
  ReferenceField,
  TextInput,
  FormDataConsumer,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  SelectInput,
  DateField,
  useEditContext,
  ReferenceInput,
  AutocompleteInput,
  useTranslate,
} from 'react-admin';

// Components
import EmptyList from '../components/misc/EmptyList';
import SelectUserByEmailInput from '../components/form/SelectUserByEmailInput';
import DynamicCell from '../components/misc/DynamicCell';
import ResizableDatagrid from '../components/table/ResizableDatagrid';

// Entity
const entity = 'promoters';

export const PromoterList = (props) => {
  const translate = useTranslate();
  const filters = [
    <ReferenceInput
      source="organisationCompanies"
      reference="organisationCompanies"
      label={translate('promoters.filter_org', { _: 'Org. Şirketi Filtresi' })}
      sort={{ field: 'name', order: 'ASC' }}
      alwaysOn
      key="org"
    >
      <AutocompleteInput
        optionText={'name'}
        matchSuggestion={() => true}
        filterToQuery={(searchText) => ({ name: searchText })}
        label={translate('promoters.company_name', { _: 'Şirket Adı' })}
        style={{ minWidth: 200 }}
      />
    </ReferenceInput>,
    <ReferenceInput
      source="venues"
      reference="venues"
      label={translate('promoters.filter_venue', { _: 'Mekan Filtresi' })}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={200}
      alwaysOn
      key="venue"
    >
      <AutocompleteInput
        optionText={'name'}
        matchSuggestion={() => true}
        filterToQuery={(searchText) => ({ name: searchText })}
        label={translate('promoters.venue_name', { _: 'Mekan Adı' })}
        style={{ minWidth: 150 }}
      />
    </ReferenceInput>,
  ];

  return (
    <List
      {...props}
      empty={<EmptyList entity={entity} />}
      title={translate('promoters.title', { _: 'Organizörler / Promotörler' })}
      filters={filters}
    >
      <ResizableDatagrid storageKey="gk-datagrid-widths:promoters">
        <ReferenceField
          source="user"
          reference="users"
          label={translate('promoters.user', { _: 'Kullanıcı' })}
          link={false}
        >
          <FunctionField render={(r) => `${r.name} ${r.surname}`} />
        </ReferenceField>
        <ReferenceField
          source="user"
          reference="users"
          label={translate('promoters.mobile', { _: 'Telefon' })}
          link={false}
        >
          <FunctionField render={(r) => `${r.mobile}`} />
        </ReferenceField>
        <FunctionField
          source="organisationCompanies"
          label={translate('promoters.belongs_to', { _: 'Ait Olduğu Kurum' })}
          render={(r) => {
            if (r.organisationCompanies && r.organisationCompanies.length > 0) {
              return r.organisationCompanies.map((v, i) => {
                return (
                  <DynamicCell
                    key={i}
                    reference={'organisationCompanies'}
                    id={v}
                    field={'name'}
                    suffix={' (Org. Company)'}
                  />
                );
              });
            } else if (r.venues && r.venues.length > 0) {
              return r.venues.map((v, i) => {
                return (
                  <DynamicCell
                    key={i}
                    reference={'venues'}
                    id={v}
                    field={'name'}
                    suffix={' (Venue)'}
                  />
                );
              });
            }
          }}
        />
        <DateField
          source="createdate"
          label={translate('promoters.created_on', { _: 'Oluşturulma Tarihi' })}
        />
        <EditButton />
      </ResizableDatagrid>
    </List>
  );
};

export const PromoterCreate = (props) => {
  const translate = useTranslate();
  return (
    <Create title={translate('ra.action.create', { _: 'Oluştur' })} {...props} redirect={'list'}>
      <SimpleForm redirect={`/${entity}`}>
        <h2>{translate('promoters.user_selection', { _: 'Kullanıcı Seçimi' })}</h2>
        <SelectUserByEmailInput source={'user'} required={true} />

        <h2>{translate('promoters.details', { _: 'Detaylar' })}</h2>
        <SelectInput
          source="promoterType"
          label={translate('promoters.promoter_type', { _: 'Promotör Türü' })}
          value={'organisationCompany'}
          choices={[
            {
              id: 'organisationCompany',
              name: translate('promoters.type_org', { _: 'Organizasyon Şirketi' }),
            },
            { id: 'venue', name: translate('promoters.type_venue', { _: 'Mekan' }) },
          ]}
          fullWidth
          defaultValue={'organisationCompany'}
          emptyValue={'organisationCompany'}
          emptyText={translate('promoters.please_select', { _: 'Lütfen Seçiniz' })}
        />

        <FormDataConsumer>
          {({ formData }) => {
            if (formData && formData.promoterType === 'organisationCompany') {
              return (
                <ReferenceArrayInput
                  source="organisationCompanies"
                  reference="organisationCompanies"
                  label={translate('promoters.org_company', { _: 'Organizasyon Şirketi' })}
                  defaultValue={null}
                  sort={{
                    field: 'name',
                    order: 'ASC',
                  }}
                >
                  <AutocompleteArrayInput
                    optionText="name"
                    fullWidth
                    matchSuggestion={() => true}
                    filterToQuery={(searchText) => ({ name: searchText })}
                    defaultValue={null}
                    clearOnBlur={false}
                  />
                </ReferenceArrayInput>
              );
            }
          }}
        </FormDataConsumer>

        <FormDataConsumer>
          {({ formData }) => {
            if (formData && formData.promoterType === 'venue') {
              return (
                <ReferenceArrayInput
                  source="venues"
                  reference="venues"
                  label={translate('promoters.venue', { _: 'Mekan' })}
                  defaultValue={null}
                  sort={{ field: 'name', order: 'ASC' }}
                >
                  <AutocompleteArrayInput
                    optionText="name"
                    fullWidth
                    matchSuggestion={() => true}
                    filterToQuery={(searchText) => ({ name: searchText })}
                    defaultValue={null}
                    clearOnBlur={false}
                  />
                </ReferenceArrayInput>
              );
            }
          }}
        </FormDataConsumer>

        <TextInput
          source={'notes'}
          label={translate('promoters.notes', { _: 'Notlar' })}
          fullWidth
          multiline
          rows={5}
          defaultValue={null}
          placeholder={translate('promoters.notes_placeholder', {
            _: 'Promotör ile ilgili notlarınızı buraya yazabilirsiniz..',
          })}
        />
      </SimpleForm>
    </Create>
  );
};

export const PromoterEdit = (props) => {
  const translate = useTranslate();
  return (
    <Edit title={translate('ra.action.edit', { _: 'Düzenle' })} {...props} redirect={'list'}>
      <SimpleForm redirect={`/${entity}`}>
        <EditForm />
      </SimpleForm>
    </Edit>
  );
};

const EditForm = (props) => {
  const translate = useTranslate();
  const { record, isLoading } = useEditContext();
  if (isLoading) return null;

  return (
    <React.Fragment>
      <h2>{translate('promoters.user', { _: 'Kullanıcı' })}</h2>

      <SelectUserByEmailInput source={'user'} required={true} record={record} />

      <h2>{translate('promoters.details', { _: 'Detaylar' })}</h2>

      <ReferenceArrayInput
        source="organisationCompanies"
        reference="organisationCompanies"
        label={translate('promoters.org_company', { _: 'Organizasyon Şirketi' })}
        defaultValue={null}
        sort={{ field: 'name', order: 'ASC' }}
      >
        <AutocompleteArrayInput
          optionText="name"
          fullWidth
          matchSuggestion={() => true}
          filterToQuery={(searchText) => ({ name: searchText })}
          defaultValue={null}
          clearOnBlur={false}
        />
      </ReferenceArrayInput>

      <ReferenceArrayInput
        source="venues"
        reference="venues"
        label={translate('promoters.venue', { _: 'Mekan' })}
        defaultValue={null}
        sort={{ field: 'name', order: 'ASC' }}
      >
        <AutocompleteArrayInput
          optionText="name"
          fullWidth
          matchSuggestion={() => true}
          filterToQuery={(searchText) => ({ name: searchText })}
          defaultValue={null}
          clearOnBlur={false}
        />
      </ReferenceArrayInput>

      <TextInput
        source={'notes'}
        label={translate('promoters.notes', { _: 'Notlar' })}
        fullWidth
        multiline
        rows={5}
        defaultValue={null}
        placeholder={translate('promoters.notes_placeholder', {
          _: 'Promotör ile ilgili notlarınızı buraya yazabilirsiniz..',
        })}
      />
    </React.Fragment>
  );
};
